// MagicaVoxel-style box-draw preview. While a box_draw gesture is dragging, the
// grid is NOT mutated (that would mean writing — and re-meshing — up to millions
// of cells per frame). Instead we draw only the box's 6 OUTER faces as a shell:
// solid faces in the active palette colour, the cell grid on each face, and a
// dark edge outline. For a solid box the surface is all you ever see, so the
// shell is visually indistinguishable from the committed voxels — but it rebuilds
// in O(box edge length), flat regardless of how many cells the box contains. The
// real fill happens once on release (see commitBoxDraw in interaction/box.ts).

import * as THREE from "three";
import { CELL_SIZE } from "../model/voxel-grid";
import { GRID_LINE_COLOR } from "./voxel-mesh";
import type { CellBounds } from "../interaction/pick";

const EDGE_LINE_COLOR = 0x000000;

interface Cell {
  ix: number;
  iy: number;
  iz: number;
}

const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

// CCW-from-outside winding matching voxel-mesh's addQuad, so the preview faces
// light identically to real voxels under the editor rig.
function addQuad(
  pos: number[], norm: number[], idx: number[],
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
  dx: number, dy: number, dz: number,
  nx: number, ny: number, nz: number,
): void {
  const base = pos.length / 3;
  pos.push(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz);
  norm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz, nx, ny, nz);
  idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
}

const line = (out: number[], ax: number, ay: number, az: number, bx: number, by: number, bz: number): void => {
  out.push(ax, ay, az, bx, by, bz);
};

export class BoxPreview {
  private _group = new THREE.Group();
  private faceMesh: THREE.Mesh;
  private gridLines: THREE.LineSegments;
  private edgeLines: THREE.LineSegments;
  private faceMat: THREE.MeshStandardMaterial;
  private gridMat: THREE.LineBasicMaterial;
  private edgeMat: THREE.LineBasicMaterial;
  // Cache the last shown box + colour so a drag frame that doesn't change the
  // AABB skips the geometry rebuild.
  private key = "";

  constructor() {
    this.faceMat = new THREE.MeshStandardMaterial({
      roughness: 0.85,
      metalness: 0.0,
      // Depth bias pushes the faces back so the grid/edge lines drawn on top win
      // the depth test. Less bias than the real voxel faces (factor 2) so that
      // while the preview is HELD over the still-building real mesh, the preview
      // faces sit consistently in front and cleanly occlude it (no z-fighting)
      // until the shell is hidden.
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
    this.faceMesh = new THREE.Mesh(new THREE.BufferGeometry(), this.faceMat);

    this.gridMat = new THREE.LineBasicMaterial({
      color: GRID_LINE_COLOR,
      transparent: true,
      opacity: 1.0,
    });
    this.gridMat.depthFunc = THREE.LessEqualDepth;
    this.gridLines = new THREE.LineSegments(new THREE.BufferGeometry(), this.gridMat);
    this.gridLines.renderOrder = 10;

    this.edgeMat = new THREE.LineBasicMaterial({
      color: EDGE_LINE_COLOR,
      transparent: true,
      opacity: 1.0,
    });
    this.edgeMat.depthFunc = THREE.LessEqualDepth;
    this.edgeLines = new THREE.LineSegments(new THREE.BufferGeometry(), this.edgeMat);
    this.edgeLines.renderOrder = 11;

    this._group.add(this.faceMesh, this.gridLines, this.edgeLines);
    this._group.visible = false;
  }

  get object3D(): THREE.Object3D {
    return this._group;
  }

  hide(): void {
    if (this._group.visible) this._group.visible = false;
  }

  // Match the mesher's zoom-aware grid/edge line fade so the preview shell is
  // visually identical to the committed voxels — otherwise the shell (full
  // opacity) looks darker than the faded real mesh, and the box appears to
  // change the moment the real mesh swaps in. Mirrors VoxelMesher.setLineFade.
  setLineFade(gridFade: number, edgeFade: number): void {
    if (this.gridMat.opacity !== gridFade) this.gridMat.opacity = gridFade;
    if (this.edgeMat.opacity !== edgeFade) this.edgeMat.opacity = edgeFade;
  }

  // Respect the grid/edge SHOW toggles, so the box-draw shell matches what the
  // committed voxels will show (and doesn't flash grid lines the user hid).
  setLineVisible(grid: boolean, edges: boolean): void {
    this.gridLines.visible = grid;
    this.edgeLines.visible = edges;
  }

  // Show/update the preview for the box spanning cells [a..b] (inclusive),
  // clamped to the work volume so it matches what release will actually
  // fill/erase. style "fill" = solid voxel shell; "erase" = translucent region
  // (it's about to be removed, so it mustn't read as added geometry).
  update(
    a: Cell,
    b: Cell,
    color: THREE.Color,
    bounds: CellBounds,
    style: "fill" | "erase" = "fill",
  ): void {
    const ix0 = clamp(Math.min(a.ix, b.ix), bounds.minIx, bounds.maxIx - 1);
    const ix1 = clamp(Math.max(a.ix, b.ix), bounds.minIx, bounds.maxIx - 1);
    const iy0 = clamp(Math.min(a.iy, b.iy), bounds.minIy, bounds.maxIy - 1);
    const iy1 = clamp(Math.max(a.iy, b.iy), bounds.minIy, bounds.maxIy - 1);
    const iz0 = clamp(Math.min(a.iz, b.iz), bounds.minIz, bounds.maxIz - 1);
    const iz1 = clamp(Math.max(a.iz, b.iz), bounds.minIz, bounds.maxIz - 1);
    if (ix1 < ix0 || iy1 < iy0 || iz1 < iz0) {
      this.hide();
      return;
    }

    this._group.visible = true;
    if (this.faceMat.color.getHex() !== color.getHex()) this.faceMat.color.copy(color);
    const erase = style === "erase";
    if (this.faceMat.transparent !== erase) {
      this.faceMat.transparent = erase;
      this.faceMat.depthWrite = !erase;
      this.faceMat.needsUpdate = true;
    }
    this.faceMat.opacity = erase ? 0.28 : 1.0;

    const key = `${ix0},${iy0},${iz0},${ix1},${iy1},${iz1}`;
    if (key === this.key) return; // AABB unchanged — geometry still valid
    this.key = key;

    const x0 = ix0 * CELL_SIZE, x1 = (ix1 + 1) * CELL_SIZE;
    const y0 = iy0 * CELL_SIZE, y1 = (iy1 + 1) * CELL_SIZE;
    const z0 = iz0 * CELL_SIZE, z1 = (iz1 + 1) * CELL_SIZE;

    // ── Faces (6 quads) ──
    const pos: number[] = [], norm: number[] = [], idx: number[] = [];
    addQuad(pos, norm, idx, x0, y0, z0, x0, y1, z0, x0, y1, z1, x0, y0, z1, -1, 0, 0);
    addQuad(pos, norm, idx, x1, y0, z1, x1, y1, z1, x1, y1, z0, x1, y0, z0, 1, 0, 0);
    addQuad(pos, norm, idx, x0, y0, z1, x1, y0, z1, x1, y0, z0, x0, y0, z0, 0, -1, 0);
    addQuad(pos, norm, idx, x0, y1, z0, x1, y1, z0, x1, y1, z1, x0, y1, z1, 0, 1, 0);
    addQuad(pos, norm, idx, x1, y0, z0, x1, y1, z0, x0, y1, z0, x0, y0, z0, 0, 0, -1);
    addQuad(pos, norm, idx, x0, y0, z1, x0, y1, z1, x1, y1, z1, x1, y0, z1, 0, 0, 1);

    // Cell-boundary positions along each axis (one per cell line, inclusive).
    const xs: number[] = [], ys: number[] = [], zs: number[] = [];
    for (let i = ix0; i <= ix1 + 1; i++) xs.push(i * CELL_SIZE);
    for (let i = iy0; i <= iy1 + 1; i++) ys.push(i * CELL_SIZE);
    for (let i = iz0; i <= iz1 + 1; i++) zs.push(i * CELL_SIZE);

    // ── Grid lines on each face (merged: one segment per cell boundary) ──
    const g: number[] = [];
    for (const xf of [x0, x1]) {
      for (const y of ys) line(g, xf, y, z0, xf, y, z1);
      for (const z of zs) line(g, xf, y0, z, xf, y1, z);
    }
    for (const yf of [y0, y1]) {
      for (const x of xs) line(g, x, yf, z0, x, yf, z1);
      for (const z of zs) line(g, x0, yf, z, x1, yf, z);
    }
    for (const zf of [z0, z1]) {
      for (const x of xs) line(g, x, y0, zf, x, y1, zf);
      for (const y of ys) line(g, x0, y, zf, x1, y, zf);
    }

    // ── Edge outline (the 12 box edges) ──
    const e: number[] = [];
    line(e, x0, y0, z0, x1, y0, z0); line(e, x0, y1, z0, x1, y1, z0);
    line(e, x0, y0, z1, x1, y0, z1); line(e, x0, y1, z1, x1, y1, z1);
    line(e, x0, y0, z0, x0, y1, z0); line(e, x1, y0, z0, x1, y1, z0);
    line(e, x0, y0, z1, x0, y1, z1); line(e, x1, y0, z1, x1, y1, z1);
    line(e, x0, y0, z0, x0, y0, z1); line(e, x1, y0, z0, x1, y0, z1);
    line(e, x0, y1, z0, x0, y1, z1); line(e, x1, y1, z0, x1, y1, z1);

    this.setGeom(this.faceMesh, pos, norm, idx);
    this.setLines(this.gridLines, g);
    this.setLines(this.edgeLines, e);
  }

  private setGeom(mesh: THREE.Mesh, pos: number[], norm: number[], idx: number[]): void {
    mesh.geometry.dispose();
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute("normal", new THREE.Float32BufferAttribute(norm, 3));
    geo.setIndex(idx);
    geo.computeBoundingSphere();
    mesh.geometry = geo;
  }

  private setLines(mesh: THREE.LineSegments, pos: number[]): void {
    mesh.geometry.dispose();
    mesh.geometry = new THREE.BufferGeometry().setAttribute(
      "position",
      new THREE.Float32BufferAttribute(pos, 3),
    );
  }
}
